/**
 * FieldListItem — single selectable field row in the picker.
 *
 * Shows field label with:
 * - inline `fx` badge (with hover popover) for calculated fields
 * - right-aligned cardinality label for dimensions/dates
 */
import { useCallback } from "react";
import { makeStyles, tokens, mergeClasses } from "@fluentui/react-components";
import {
  NumberSymbolRegular,
  TextNumberListLtrRegular,
  CalendarRegular,
  KeyRegular,
  QuestionCircleRegular,
} from "@fluentui/react-icons";
import type { DatasetViewColumn } from "../../../../api/types";
import FxPopover from "./FxPopover";
import JsonProvenanceBadge from "./JsonProvenanceBadge";

// ── Semantic type icon mapping ──────────────────────────────────────────────

function SemanticIcon({ semanticType }: { semanticType: string }) {
  switch (semanticType) {
    case "measure":
      return <NumberSymbolRegular />;
    case "time":
      return <CalendarRegular />;
    case "id":
    case "identifier":
      return <KeyRegular />;
    case "dimension":
    case "category":
      return <TextNumberListLtrRegular />;
    default:
      return <QuestionCircleRegular />;
  }
}

// ── Cardinality formatting ──────────────────────────────────────────────────

function formatCardinality(count: number): string {
  if (count < 1000) return `${count} values`;
  if (count < 1_000_000) {
    const k = count / 1000;
    return k >= 10 ? `${Math.round(k)}K values` : `${k.toFixed(1)}K values`;
  }
  const m = count / 1_000_000;
  return m >= 10 ? `${Math.round(m)}M values` : `${m.toFixed(1)}M values`;
}

const useStyles = makeStyles({
  row: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 12px 5px 12px",
    cursor: "pointer",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    borderRadius: tokens.borderRadiusMedium,
    margin: "0 4px",
    ":hover": {
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
  },
  selected: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  icon: {
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },
  label: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginLeft: "auto",
    paddingLeft: "8px",
    flexShrink: 0,
  },
  cardinality: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    whiteSpace: "nowrap",
    minWidth: "60px",
    textAlign: "right" as const,
  },
  fxSlot: {
    width: "20px",
    display: "flex",
    justifyContent: "center",
    flexShrink: 0,
  },
});

interface FieldListItemProps {
  column: DatasetViewColumn;
  isSelected: boolean;
  onSelect: (colName: string) => void;
}

export default function FieldListItem({ column, isSelected, onSelect }: FieldListItemProps) {
  const styles = useStyles();

  const handleClick = useCallback(() => {
    onSelect(column.name);
  }, [column.name, onSelect]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(column.name);
      }
    },
    [column.name, onSelect],
  );

  const displayLabel = column.display_name || column.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Show cardinality for dimensions and dates only
  const showCardinality =
    column.distinct_count != null &&
    column.distinct_count > 0 &&
    (column.semantic_type === "dimension" || column.semantic_type === "time");

  return (
    <div
      className={mergeClasses(styles.row, isSelected && styles.selected)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="option"
      aria-selected={isSelected}
      tabIndex={0}
    >
      <span className={styles.icon}>
        <SemanticIcon semanticType={column.semantic_type} />
      </span>
      <span className={styles.label}>{displayLabel}</span>
      <span className={styles.meta}>
        {column.source_kind === "json" && <JsonProvenanceBadge column={column} />}
        {showCardinality && (
          <span className={styles.cardinality}>
            {formatCardinality(column.distinct_count!)}
          </span>
        )}
        <span className={styles.fxSlot} onClick={(e) => e.stopPropagation()}>
          {column.is_calculated && (
            <FxPopover label={displayLabel} formula={column.formula} formulaDescription={column.formula_description} />
          )}
        </span>
      </span>
    </div>
  );
}
